import React from "react";
import {
  Box,
  Button,
  IconButton,
  Modal,
  Typography,
  useTheme,
  Chip,
  Grid,
  Paper
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import { Supplier } from "./types/supplier";
import { SupplierStatus } from "./enums/supplierStatus";
import {
  BRAND_COLOR,
  BRAND_HOVER,
  useKivunovaTranslation
} from "@kivunova/kivufrontendcommon";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useDeleteSupplier } from "../../hooks/api/useSupplier";
import { LoadingButton } from "@mui/lab";

interface ViewSupplierModalProps {
  open: boolean;
  onClose: () => void;
  supplier: Supplier;
  onDelete?: () => void;
}

const ViewSupplierModal: React.FC<ViewSupplierModalProps> = ({
  open,
  onClose,
  supplier,
  onDelete
}) => {
  const theme = useTheme();
  const matchMD = useMediaQuery(theme.breakpoints.down("md"));
  const deleteSupplier = useDeleteSupplier();
  const { t } = useKivunovaTranslation();

  const handleDelete = async () => {
    try {
      await deleteSupplier.mutateAsync(supplier.id);
      if (onDelete) {
        onDelete();
      }
      onClose();
    } catch (err) {
      console.error("❌ Failed to delete supplier:", err);
    }
  };

  const getStatusColor = (status: SupplierStatus) => {
    switch (status) {
      case SupplierStatus.ACTIVE:
        return "success";
      case SupplierStatus.SUSPENDED:
        return "warning";
      case SupplierStatus.DELETED:
        return "error";
      default:
        return "default";
    }
  };

  // Build fields array for grid display
  const fields: [string, string | React.ReactNode | undefined][] = [
    [t("supplier-field-name", "Name"), supplier.name],
    [
      t("supplier-field-status", "Status"),
      <Chip
        key="status"
        label={supplier.status}
        size="small"
        color={getStatusColor(supplier.status)}
      />
    ],
    ...(supplier.phone
      ? [[t("supplier-field-phone", "Phone"), supplier.phone]]
      : []),
    ...(supplier.email
      ? [[t("supplier-field-email", "Email"), supplier.email]]
      : []),
    ...(supplier.address?.country
      ? [[t("supplier-field-country", "Country"), supplier.address.country]]
      : []),
    ...(supplier.address?.province
      ? [[t("supplier-field-province", "Province"), supplier.address.province]]
      : []),
    ...(supplier.address?.district
      ? [[t("supplier-field-district", "District"), supplier.address.district]]
      : []),
    ...(supplier.address?.sector
      ? [[t("supplier-field-sector", "Sector"), supplier.address.sector]]
      : []),
    ...(supplier.address?.cell
      ? [[t("supplier-field-cell", "Cell"), supplier.address.cell]]
      : []),
    ...(supplier.address?.village
      ? [[t("supplier-field-village", "Village"), supplier.address.village]]
      : []),
    ...(supplier.address?.streetAddress
      ? [
          [
            t("supplier-field-street", "Street Address"),
            supplier.address.streetAddress
          ]
        ]
      : [])
  ];

  const modalStyle = {
    position: "absolute" as const,
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: matchMD ? "95%" : 700,
    maxHeight: "90vh",
    overflowY: "auto" as const,
    bgcolor: "background.paper",
    boxShadow: 24,
    borderRadius: 2,
    p: 4
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={modalStyle}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={3}
        >
          <Typography variant="h5" fontWeight="bold" color="textPrimary">
            {t("supplier-view-title", "Supplier Details")}
          </Typography>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Detail Card */}
        <Paper
          elevation={1}
          sx={{
            p: 2,
            borderRadius: 2,
            overflow: "hidden",
            mb: 3
          }}
        >
          <Grid container>
            {fields.map(([label, value], index) => (
              <React.Fragment key={label}>
                <Grid
                  sx={{
                    width: "100%",
                    padding: "0 10px",
                    display: "flex",
                    borderBottom:
                      index === fields.length - 1 ? "none" : "1px solid #e0e0e0"
                  }}
                >
                  <Box
                    sx={{
                      flex: 1,
                      borderRight: "0.5px solid #e0e0e0",
                      p: 1.5,
                      bgcolor: "white"
                    }}
                  >
                    <Typography
                      variant="body2"
                      fontWeight={600}
                      color="text.secondary"
                    >
                      {label}:
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      flex: 2,
                      p: 1.5,
                      bgcolor: "white",
                      display: "flex",
                      alignItems: "center"
                    }}
                  >
                    {typeof value === "string" ? (
                      <Typography variant="body2">{value ?? "-"}</Typography>
                    ) : (
                      value
                    )}
                  </Box>
                </Grid>
              </React.Fragment>
            ))}
          </Grid>
        </Paper>

        <Box display="flex" gap={2} justifyContent="flex-end">
          <Button
            variant="outlined"
            onClick={onClose}
            sx={{
              borderColor: BRAND_COLOR,
              color: BRAND_COLOR,
              "&:hover": {
                backgroundColor: BRAND_HOVER,
                color: "white"
              },
              textTransform: "none"
            }}
          >
            {t("close", "Close")}
          </Button>
          <LoadingButton
            variant="contained"
            color="error"
            startIcon={<DeleteOutlineOutlinedIcon />}
            onClick={handleDelete}
            loading={deleteSupplier.isPending}
            sx={{ textTransform: "none" }}
          >
            {t("delete", "Delete")}
          </LoadingButton>
        </Box>
      </Box>
    </Modal>
  );
};

export default ViewSupplierModal;
